import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import styled from 'styled-components';
import { GLOBAL_ICONS, YEAR_GROUP_COLORS } from '../../../../../util/vars';
import { Button, ChoiceGroup, Loader, Tag } from '../../../../../components';
import { getIconComponent } from '../../../../../util/reactFunc';
import ButtonIcon from '../../../../../components/button/Button.icon';
import type { RosterManagementStudentListItem } from '@whizz/api-model';
import { getDefaultTagColor } from '../../../../../util/styled';
import { ValueHeadline } from '../../../../Home/modules';
import useYearGroups from '../../../../../hooks/useYearGroups';

const getGridCols = (max: number) => {
  const gridClasses = [
    'grid-cols-1',
    'grid-cols-2',
    'grid-cols-3',
    'grid-cols-4',
    'grid-cols-5',
    'grid-cols-6',
    'grid-cols-7',
    'grid-cols-8',
    'grid-cols-9',
    'grid-cols-10',
    'grid-cols-11',
    'grid-cols-12',
  ];
  return gridClasses[Math.min(max, 12) - 1];
};

export const YearGroup = ({ yearGroupId }: { yearGroupId: RosterManagementStudentListItem['yearGroupId'] }): JSX.Element | null => {
  const [ , getYearGroupById ] = useYearGroups();
  const yearGroup = yearGroupId ?? 'NO_YEAR_GROUP';
  return (
    <Tag isOutline color={YEAR_GROUP_COLORS[yearGroup]}>
      {getYearGroupById(yearGroupId).name}
    </Tag>
  );
};

export const Groups = ({ groups, max = 1 }: { groups: RosterManagementStudentListItem['classes'], max?: number }): JSX.Element => {
  return (
    <div className={`grid ${getGridCols(max)} gap-1`}>
      {groups.map((group) => {
        return (
          <Tag key={group.id} color={group.color ?? getDefaultTagColor(group.id)} >
            {group.name}
          </Tag>
        );})}
    </div>
  );
};

export const Tags = ({ tags, max = 1 }: { tags: RosterManagementStudentListItem['tags'], max?: number }): JSX.Element => {
  const { translate } = useI18n();
  return (
    <div className={`grid ${getGridCols(max)} gap-1`}>
      {tags.map((tag) => {
        return (
          <Tag key={tag.id} color={tag.color ?? getDefaultTagColor(tag.id)} >
            {translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tag.abbreviation}`, { 'defaultValue': tag.abbreviation })}
          </Tag>
        );})}
    </div>
  );
};

export const Checkbox = ({ id, checked, onChange, disabled }: { id: any, checked: boolean, onChange: () => void, disabled?: boolean }): JSX.Element => (
  <div className={'flex justify-between items-center'}>
    <ChoiceGroup
      id={`student_${id}`}
      label={''}
      hideLabel
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  </div>
);

export const Edit = ({ onClick } : { onClick: () => void }):JSX.Element => (
  <div className={'flex justify-between items-center'}>
    <ButtonIcon
      icon={GLOBAL_ICONS.edit}
      size={'sm'}
      asLink
      onClick={() => onClick()}
    />
  </div>
);

export const Active = ({ active }: { active: boolean }): JSX.Element => (
  <>
    {getIconComponent(active ? 'Lightbulb' : 'LightbulbOff', 40, active ? 'secondary' : 'muted')}
  </>
);

export const ContinueBlockStyled = styled('div')`
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 125px;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Continue = ({ onClick, isLoading = false, count }: { onClick: () => void, isLoading?: boolean, count: number }): JSX.Element | null => {
  const { translate } = useI18n();

  return (
    <ContinueBlockStyled>
      <div className={'flex justify-center items-center gap-6 bg-white rounded-default py-6 px-14'}>
        <ValueHeadline value={count} description={translate('pages.ROSTER_MANAGEMENT.STUDENTS.SELECTED_STUDENTS', { count })} showMoreInfo={false} colorValue className={'flex items-center'}/>
        <Button
          isOutline
          color={'positive'}
          icon={'ArrowForward'}
          size={'lg'}
          onClick={onClick}
          disabled={isLoading}
        >
          {!isLoading
            ? translate('components.BUTTONS.CONTINUE')
            : (<Loader disableLabel smallSize />)
          }
        </Button>
      </div>
    </ContinueBlockStyled>
  );
};
